/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Trans, useTranslation } from 'react-i18next';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import clsx from 'clsx';

import { useMenuScopeIdentifier } from 'hooks/menuScope';
import AlertMessage from '@experimental-components/AlertMessage';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';

import Tabs from '@design-system/Tabs';
import InputAdornmentFiled from '@design-system/InputAdornmentFiled';
import SelectDS from '@design-system/Select';

import ImageDropzone from 'components/ImageDropzone';
import MultiSelect from 'components/MultiSelect';
import SettingsAdvanceTaxes from 'components/SettingsAdvanceTaxes';
import SkuMultiStore from 'components/SkuMultiStore';
import SelectColor from 'components/SelectColor';

import useEnvironment from 'hooks/useEnvironment';

import { tabsFormProduct, tabsFormOptions, getNameProductDuplicateBarcode } from 'utils/products';
import { taxServingUnitTypes } from 'utils/products/taxTypes';
import { regexTypes, formatTextAllowedValues } from 'utils/formatField';
import {
  validateUppercase,
  handleFormatValidateItems,
  validateDuplicateSkuItems,
  getInitialOptionsToMultiSelect,
} from 'utils/menuMaker';

import {
  actionsTypes,
  getMergeKitchenAreasAdd,
  getMergeKitchenAreasRemove,
  getCategoriesChanges,
  getKitchenAreasDetail,
} from 'utils/kitchenAreas/makeKitchenAreasFields';

import { getErrorCode, errorsCodeTypes } from 'utils/parrotErrorCode';

import TabPanel from './TabPanel';
import KitchenAreasAsigned from './KitchenAreasAsigned';
import KitchenAreasBox from './kitchenAreasBox';
import ConfirmDialog from './modalConfirm';
import ProductSkeleton from './ProductSkeleton';
import SatProductKeyPicker from './SatProductKeyPicker';
import useStyles from './styles';
import SoldBehaviourSection from './SoldBehaviourSection';
import { SalesTypes } from './types';

// Generic SAT product/service key for "Establecimientos para comer y beber". The
// backend already applies this when no explicit key is sent, so we surface it as
// the "default" the user can restore to from the picker.
const DEFAULT_SAT_PRODUCT_KEY = {
  code: '90101500',
  description: 'Establecimientos para comer y beber',
};

function ProductForm({
  canSelectOtherAreas,
  cantPrintKA,
  categoriesState,
  defaultTaxes = {},
  isEdition,
  isLoaded,
  isLoading,
  isOrganizationMode,
  kitchenAreasState,
  menusProductOverride = [],
  modifierGroupsState,
  multiTaxes,
  onSetStep,
  openDrawer,
  selectedCategories,
  setCanSelectOtherAreas,
  setCantPrintKA,
  setSelectedCategories,
  uuidSelected,
  categoryOfProduct,
  shouldShowUnitSelector = false,
  actionState,
}) {
  const classes = useStyles();
  const { t } = useTranslation(['menuMaker', 'kitchenAreas']);
  const formMethods = useFormContext();
  const { register, errors, control, setValue, watch, trigger, setError } = formMethods;
  const { environmentsState } = useEnvironment();
  const { isStoreMode } = useMenuScopeIdentifier();

  const [tabSelected, setTabSelected] = useState(tabsFormOptions.GENERAL);
  const [showConfirmAreasKitchen, setShowConfirmAreasKitchen] = useState({});
  const [selectedVirtualCategories, setSelectedVirtualCategories] = useState([]);
  const [virtualKitchenAreas, setVirtualKitchenAreas] = useState([]);
  const [showConfirmChangeTypeSold, setShowConfirmChangeTypeSold] = useState({});

  const watchFields = watch();
  const flagMenuColor = environmentsState.REACT_APP_FEATURE_MENU_COLOR === 'true';

  const kitchenAreasFields = useWatch({
    control,
    name: 'kitchenAreasFields',
  });

  // The SAT product-key code + description live entirely inside the RHF form. RHF v6
  // doesn't fire useWatch subscriptions for fields that were only populated via reset()
  // (no Controller, no register), so we register them once on mount — that lets setValue
  // notify subscribers and keeps the picker's displayed value in sync with the form.
  useEffect(() => {
    register({ name: 'taxProductKeyField' });
    register({ name: 'taxProductKeyDescriptionField' });
  }, [register]);

  // Drive the picker off `watchFields` (the full-form watch() subscription already in use
  // for everything else in this form). It re-renders on every form change, including
  // setValue on the two SAT fields above, so the displayed value is always current.
  const satSelection = watchFields?.taxProductKeyField
    ? {
        code: watchFields.taxProductKeyField,
        description: watchFields.taxProductKeyDescriptionField || '',
      }
    : null;

  useEffect(() => {
    if (!openDrawer) {
      setCanSelectOtherAreas(false);
    }
  }, [openDrawer]);

  useEffect(() => {
    if (isLoading || (!isEdition && openDrawer) || categoryOfProduct) {
      setTabSelected(tabsFormOptions.GENERAL);
    }
  }, [isLoading, isEdition, categoryOfProduct]);

  useEffect(() => {
    const errorBarcode = getErrorCode(actionState?.error, errorsCodeTypes.PRODUCT_BARCODE_DUPLICATED);
    if (actionState?.error && errorBarcode) {
      setError('barcodeField', {
        type: 'custom',
        message: t('menuMaker:productForm.messages.errorBarcode', {
          name: getNameProductDuplicateBarcode(errorBarcode),
        }),
      });
      setTabSelected(tabsFormOptions.ADVANCED);
      actionState?.resetState();
    }
  }, [actionState?.error]);

  const handleChangeSelectedTab = (event, newValue) => {
    setTabSelected(newValue);
  };

  function handleChangeCategories(selectedOptions) {
    const categoriesChanges = getCategoriesChanges(selectedCategories, selectedOptions);

    if (isEdition && !canSelectOtherAreas) {
      if (categoriesChanges.actionType !== actionsTypes.ACTION_TYPE_NOTHING && !canSelectOtherAreas) {
        setShowConfirmAreasKitchen({
          message:
            categoriesChanges.actionType === actionsTypes.ACTION_TYPE_ADD
              ? t('menuMaker:dialogConfirmKitchenAreas.messageAddMode')
              : t('menuMaker:dialogConfirmKitchenAreas.messageRemoveMode'),
          show: true,
          isAddMode: categoriesChanges.actionType === actionsTypes.ACTION_TYPE_ADD,
        });
        proccessSelectedCategoriesVirtual(selectedOptions, categoriesChanges);
      } else {
        setSelectedCategories(selectedOptions);
        setValue('selectedCategories', selectedOptions, { shouldValidate: true });
      }
    } else if (isEdition && canSelectOtherAreas) {
      setSelectedCategories(selectedOptions);
      setValue('selectedCategories', selectedOptions, { shouldValidate: true });
    } else if (!isEdition && !canSelectOtherAreas) {
      proccessSelectedCategories(selectedOptions, categoriesChanges);
    } else {
      setSelectedCategories(selectedOptions);
      setValue('selectedCategories', selectedOptions, { shouldValidate: true });
    }
  }

  function renderServingUnitItem(selectedKey) {
    const renderSelected = <Box className={classes.placeholderSelected}>{t('common:actions.select')}</Box>;

    if (selectedKey) {
      const itemSelected = taxServingUnitTypes.filter((item) => item.key === selectedKey);
      return itemSelected[0]?.label;
    }

    return renderSelected;
  }

  function handleCanSelectAreas(event) {
    const { checked } = event.target;
    setCanSelectOtherAreas(checked);
    if (checked && kitchenAreasFields?.length > 0) {
      setValue('kitchenAreasFields', [], { shouldValidate: true });
    } else {
      const categoriesChanges = getCategoriesChanges([], watchFields?.selectedCategories);
      const newKitchenAreas = getMergeKitchenAreasAdd([], categoriesChanges?.kitchenAreasAssignation);
      setValue('kitchenAreasFields', newKitchenAreas, { shouldValidate: true });
    }
  }

  function proccessSelectedCategories(selectedOptions, categoriesChanges) {
    setSelectedCategories(selectedOptions);
    setValue('selectedCategories', selectedOptions, { shouldValidate: true });
    if (categoriesChanges.actionType === actionsTypes.ACTION_TYPE_NOTHING) return;

    let newKitchenAreas = [];
    if (categoriesChanges.actionType === actionsTypes.ACTION_TYPE_ADD) {
      newKitchenAreas = getMergeKitchenAreasAdd(kitchenAreasFields, categoriesChanges?.kitchenAreasAssignation);
    } else {
      newKitchenAreas = getMergeKitchenAreasRemove(
        kitchenAreasFields,
        selectedCategories,
        categoriesChanges?.kitchenAreasAssignation,
      );
    }
    setValue('kitchenAreasFields', newKitchenAreas, { shouldValidate: true });
  }

  function proccessSelectedCategoriesVirtual(selectedOptions, categoriesChanges) {
    setSelectedVirtualCategories(selectedOptions);
    let newKitchenAreas = [];
    if (categoriesChanges.actionType === actionsTypes.ACTION_TYPE_ADD) {
      const defaultConfig = {};
      newKitchenAreas = getMergeKitchenAreasAdd(kitchenAreasFields, categoriesChanges?.kitchenAreasAssignation);

      if (kitchenAreasFields?.length > 0) {
        newKitchenAreas.forEach((kictehArea) => {
          defaultConfig[`${kictehArea}Field`] = true;
        });
      }
    } else {
      newKitchenAreas = getMergeKitchenAreasRemove(
        kitchenAreasFields,
        selectedCategories,
        categoriesChanges?.kitchenAreasAssignation,
      );
    }
    setVirtualKitchenAreas(newKitchenAreas);
  }

  function handleCancelConfirmNewAreasKitchen() {
    setShowConfirmAreasKitchen({ show: false });
    setSelectedVirtualCategories([]);
    setVirtualKitchenAreas([]);
  }

  function handleConfirmNewAreasKitchen() {
    setSelectedCategories(selectedVirtualCategories);
    setValue('selectedCategories', selectedVirtualCategories, { shouldValidate: true });
    handleCancelConfirmNewAreasKitchen();
    setValue('kitchenAreasFields', virtualKitchenAreas, { shouldValidate: true });
  }

  function handleChangeTypeSold(value) {
    if (isEdition && value !== SalesTypes.BY_GRAMMAGE) {
      const titleModal = value === SalesTypes.ALONE ? 'titleSoldAlone' : 'titleNotSoldAlone';
      const messageModal = value === SalesTypes.ALONE ? 'messageSoldAlone' : 'messageNotSoldAlone';
      setShowConfirmChangeTypeSold({ show: true, value, titleModal, messageModal });
    } else {
      setValue('soldAloneField', value, { shouldValidate: true });
      handleResetValuesSoldAlone();
    }
  }

  function handleConfirmChangeTypeSold() {
    handleResetValuesSoldAlone();
    setValue('soldAloneField', showConfirmChangeTypeSold.value, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setShowConfirmChangeTypeSold({ show: false });
  }

  function handleCancelChangeTypeSold() {
    setShowConfirmChangeTypeSold({ show: false });
  }

  function handleResetValuesSoldAlone() {
    setSelectedCategories([]);
    setCanSelectOtherAreas(false);
    setCantPrintKA(false);
    setValue('selectedCategories', [], {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('selectedModifierGroups', [], {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('kitchenAreasFields', [], {
      shouldValidate: true,
      shouldDirty: true,
    });

    setValue('exemptField', false, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('taxGroupFieldIVA', defaultTaxes?.taxGroupFieldIVA ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('taxGroupFieldIEPS', defaultTaxes?.taxGroupFieldIEPS ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
  }

  const handlePreventNewLine = (e) => {
    if (e.which === 13 && !e.shiftKey) {
      e.preventDefault();
      return false;
    }
  };

  const handleValidateAllowedValues = (target, field) => {
    target.value = formatTextAllowedValues(target.value, regexTypes.REGEX_PRODUCT_NAME_ALLOWED);
    setValue(field, target.value);
    return target.value;
  };
  const handleOpenSelectSKU = () => {
    onSetStep(2);
  };

  const handleValidateChars = (e) => {
    const regex = /^[a-zA-Z0-9-]+$/;
    const key = String.fromCharCode(e.keyCode || e.which);
    if (!regex.test(key)) {
      e.preventDefault();
      return false;
    }
  };

  const handleOnChangeBarcodeField = (target, field) => {
    const valueUppercase = target.value;
    target.value = valueUppercase.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase();
    setValue(field, target.value);
    return target.value;
  };

  const renderTitle = () => {
    return (
      <Typography className={classes.title}>
        {t(`menuMaker:sections.${isEdition ? 'editProduct' : 'addProduct'}`)}
      </Typography>
    );
  };

  if (isLoading) {
    return (
      <ProductSkeleton>
        <ProductSkeleton.Title>{renderTitle()}</ProductSkeleton.Title>
      </ProductSkeleton>
    );
  }

  if (isLoaded) {
    return (
      <Grid container direction="column" spacing={3}>
        <Grid item>{renderTitle()}</Grid>
        {isStoreMode && isEdition && !menusProductOverride?.length && (
          <Grid item>
            <Box pl={0} pt={3}>
              <AlertMessage severity="info" sizeIcon={20}>
                <Box>
                  <Trans i18nKey={t(`menuMaker:headerForm.store.notifications.info`)} />
                </Box>
              </AlertMessage>
            </Box>
          </Grid>
        )}
        <Box className={classes.tabs}>
          <Tabs
            indicatorColor="primary"
            onChange={handleChangeSelectedTab}
            scrollButtons="auto"
            textColor="primary"
            value={tabSelected}
            variant="scrollable"
          >
            {tabsFormProduct.map((option) => (
              <Tabs.Tab
                data-testid={option.testId}
                key={`form-product-tab-${option.value}`}
                label={t(option.label)}
                value={option.value}
              />
            ))}
          </Tabs>
        </Box>

        <TabPanel index={tabsFormOptions.GENERAL} mt={3} pl={3} value={tabSelected}>
          <Grid container direction="column" item spacing={3}>
            <Grid item>
              <InputAdornmentFiled
                autoComplete="off"
                autoFocus
                data-testid="inputNameProduct"
                disabled={isStoreMode}
                endAdornment={
                  <InputAdornment position="end">
                    <Box color="#CECDD1" fontSize="14px">
                      {watch('nameProductField')?.length ?? 0}/40
                    </Box>
                  </InputAdornment>
                }
                error={Boolean(errors?.nameProductField)}
                errorMsg={errors?.nameProductField?.message}
                fullWidth
                inputProps={{
                  maxLength: 40,
                  minLength: 3,
                  'data-testid': 'nameProduct',
                }}
                inputRef={register({
                  required: t('common:messages.requireField'),
                  maxLength: {
                    value: 40,
                    message: t('common:errors.maxLengthField', { chars: 40 }),
                  },
                  minLength: {
                    value: 3,
                    message: t('common:errors.minLength3Field'),
                  },
                  validate: {
                    controlUppercase: (value) => validateUppercase(value) || t('common:errors.controlUppercase'),
                  },
                })}
                label={t('menuMaker:productForm.tabGeneral.nameProductField')}
                name="nameProductField"
                onChange={({ target }) => handleValidateAllowedValues(target, 'nameProductField')}
              />
            </Grid>

            <Grid item>
              <Box display="grid" gap="13px" gridTemplateColumns="1fr 1fr">
                <Box>
                  <Box fontSize={14} mb={1}>
                    {t('menuMaker:productForm.tabGeneral.descriptionProductField')}
                  </Box>

                  <Box className={classes.boxesColumn}>
                    <InputAdornmentFiled
                      className={classes.baseTextArea}
                      data-testid="inputDescriptionProduct"
                      disabled={isStoreMode}
                      endAdornment={
                        <Box bottom="16px" color="#CECDD1" fontSize="14px" position="absolute" right="16px">
                          {watch('descriptionProductField')?.length ?? 0}/125
                        </Box>
                      }
                      error={Boolean(errors?.descriptionProductField)}
                      errorMsg={errors?.descriptionProductField?.message}
                      fullWidth
                      hideLabel
                      hideLabelHelperText
                      inputProps={{
                        maxLength: 125,
                        minLength: 3,
                        className: classes.textArea,
                      }}
                      inputRef={register({
                        maxLength: {
                          value: 125,
                          message: t('common:errors.maxCharsField', { chars: 125 }),
                        },
                        minLength: {
                          value: 3,
                          message: t('common:errors.minLength3Field'),
                        },
                        validate: {
                          controlUppercase: (value) => validateUppercase(value) || t('common:errors.controlUppercase'),
                        },
                      })}
                      multiline
                      name="descriptionProductField"
                      onChange={({ target }) => handleValidateAllowedValues(target, 'descriptionProductField')}
                      onKeyDown={handlePreventNewLine}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography className={classes.subtitlesThree}>
                    {t('menuMaker:productForm.tabGeneral.photo')}
                  </Typography>

                  <Box className={classes.boxesColumn}>
                    <Typography className={classes.descriptionPhoto}>
                      <Trans i18nKey="menuMaker:productForm.tabGeneral.descriptionPhoto" />
                    </Typography>
                    <Controller
                      control={control}
                      name="imageProductField"
                      render={({ onChange, value, name }) => (
                        <ImageDropzone
                          disabled={isStoreMode}
                          initialImageUrl={value}
                          isEdition={isEdition}
                          loadDisabled={false}
                          name={name}
                          onImageChange={onChange}
                          openDrawer={openDrawer}
                          value={value}
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item>
              <SoldBehaviourSection
                disabled={isStoreMode}
                formMethods={formMethods}
                isEdition={isEdition}
                menusProductOverride={menusProductOverride}
                onCancelChangeType={handleCancelChangeTypeSold}
                onChangeTypeSold={handleChangeTypeSold}
                onConfirmChangeType={handleConfirmChangeTypeSold}
                priceProductField={watchFields?.priceProductField}
                shouldShowUnitSelector={shouldShowUnitSelector}
                showConfirmChangeTypeSold={showConfirmChangeTypeSold}
                uuidSelected={uuidSelected}
              />
            </Grid>

            <Grid
              className={clsx({ [classes.hideElements]: watchFields?.soldAloneField === SalesTypes.WITH_MODIFIER })}
              item
            >
              <Box mb={3}>
                <Divider />
              </Box>

              <Typography className={classes.subtitles}>{t('menuMaker:productForm.categoryPicker.title')}</Typography>

              <Box mb={1}>
                <Typography variant="body2">{t('menuMaker:productForm.categoryPicker.inputPlaceholder')}</Typography>
              </Box>

              <Controller
                control={control}
                name="selectedCategories"
                render={({ value, name }) => (
                  <MultiSelect
                    disabled={isStoreMode}
                    getOptionLabel={(option) => option.name}
                    getOptionUniqueKey={(option) => option.uuid}
                    id="product-autocomplete-categories"
                    inputPlaceholderText={t('menuMaker:productForm.categoryPicker.inputPlaceholder')}
                    isSorteable={false}
                    name={name}
                    noOptionsText={t('menuMaker:productForm.categoryPicker.noCategories')}
                    onChange={(selectedOptions) => {
                      handleChangeCategories(handleFormatValidateItems(selectedOptions));
                    }}
                    options={getInitialOptionsToMultiSelect(categoriesState.list, value)}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} data-testid={`option-category-${option.uuid}`}>
                          {option.name}
                        </li>
                      );
                    }}
                    selectedOptions={value}
                    value={value}
                  />
                )}
                rules={{
                  validate: (items) => {
                    const itemsDuplicateSku = validateDuplicateSkuItems(items);

                    return (
                      itemsDuplicateSku?.length === 0 || t('menuMaker:skuLabels.product.errorDuplicateCategoriesSku')
                    );
                  },
                }}
              />
              {Boolean(errors?.selectedCategories) && (
                <Box className={classes.errorDuplicateSku}>{errors?.selectedCategories?.message}</Box>
              )}
            </Grid>

            <Grid className={clsx({ [classes.hideElements]: watchFields?.soldAloneField !== SalesTypes.ALONE })} item>
              <Typography className={classes.subtitles}>
                {t('menuMaker:productForm.modifierGroupsPicker.title')}
              </Typography>

              <Box mb={1}>
                <Typography variant="body2">{t('menuMaker:productForm.modifierGroupsPicker.description')}</Typography>
              </Box>

              <Controller
                control={control}
                name="selectedModifierGroups"
                render={({ onChange, value, name }) => (
                  <MultiSelect
                    disabled={isStoreMode}
                    getOptionDescription={(option) => option.description}
                    getOptionLabel={(option) => option.name}
                    getOptionUniqueKey={(option) => option.uuid}
                    id="product-autocomplete-modifiers"
                    inputPlaceholderText={t('menuMaker:productForm.modifierGroupsPicker.inputPlaceholder')}
                    name={name}
                    noOptionsText={t('menuMaker:productForm.modifierGroupsPicker.noModifiers')}
                    onChange={(selectedOptions) => onChange(handleFormatValidateItems(selectedOptions))}
                    options={getInitialOptionsToMultiSelect(modifierGroupsState.list, value)}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} data-testid={`option-modifier-${option.uuid}`}>
                          <Box>{option.name}</Box>
                          {option?.description && (
                            <Box className={classes.descriptionModifiers}>{option?.description}</Box>
                          )}
                        </li>
                      );
                    }}
                    selectedOptions={value}
                    value={value}
                  />
                )}
                rules={{
                  validate: (items) => {
                    const itemsDuplicateSku = validateDuplicateSkuItems(items);

                    return (
                      itemsDuplicateSku?.length === 0 ||
                      t('menuMaker:skuLabels.product.errorDuplicateModifierGroupsSku')
                    );
                  },
                }}
              />
              {Boolean(errors?.selectedModifierGroups) && (
                <Box className={classes.errorDuplicateSku}>{errors?.selectedModifierGroups?.message}</Box>
              )}
            </Grid>

            <Grid className={clsx({ [classes.disableSection]: !flagMenuColor })} item>
              <Box mb={3} mt={1}>
                <Divider />
              </Box>

              <Controller
                control={control}
                name="colorField"
                render={({ onChange, value }) => (
                  <SelectColor disabled={isStoreMode} entity="product" onChange={onChange} value={value} />
                )}
                rules={{ required: true }}
              />
            </Grid>

            <Grid item>
              <Box mb={4} mt={1}>
                <Divider />
              </Box>
              <Typography className={classes.subtitles}>{t('menuMaker:skuLabels.sku')}</Typography>

              <Box mb={4} mt={1}>
                <Typography color="textSecondary" variant="body2">
                  {t('menuMaker:skuLabels.product.info')}
                </Typography>
              </Box>

              <SkuMultiStore
                disabled={isStoreMode}
                errors={errors}
                labelButon="addExistingSku"
                onClickButton={handleOpenSelectSKU}
                register={register}
                trigger={isEdition ? trigger : null}
                watch={watch}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel index={tabsFormOptions.ADVANCED} mt={3} pl={3} value={tabSelected}>
          <Grid container direction="column" item spacing={3}>
            <Grid item>
              <InputAdornmentFiled
                data-testid="inputPosNameProduct"
                disabled={isStoreMode}
                endAdornment={
                  <InputAdornment position="end">
                    <Box color="#CECDD1" fontSize="14px">
                      {watch('posNameField')?.length ?? 0}/70
                    </Box>
                  </InputAdornment>
                }
                error={Boolean(errors?.posNameField)}
                errorMsg={errors?.posNameField?.message}
                fullWidth
                inputProps={{ maxLength: 70, 'data-testid': 'posNameProduct' }}
                inputRef={register({
                  maxLength: {
                    value: 70,
                    message: t('common:errors.maxLengthField', { chars: 70 }),
                  },
                })}
                label={t('menuMaker:productForm.posNameLabel')}
                name="posNameField"
              />
            </Grid>

            <Grid item>
              <InputAdornmentFiled
                disabled={isStoreMode}
                endAdornment={
                  <InputAdornment position="end">
                    <Box color="#CECDD1" fontSize="14px">
                      {watch('barcodeField')?.length ?? 0}/30
                    </Box>
                  </InputAdornment>
                }
                error={Boolean(errors?.barcodeField)}
                errorMsg={errors?.barcodeField?.message}
                fullWidth
                inputProps={{ maxLength: 30, 'data-testid': 'barcodeField' }}
                inputRef={register({
                  maxLength: {
                    value: 30,
                    message: t('common:errors.maxLengthField', { chars: 30 }),
                  },
                  minLength: {
                    value: 6,
                    message: t('common:errors.minCharsField', { chars: 6 }),
                  },
                  pattern: {
                    message: t('menuMaker:skuLabels.errorPatternCharsLabel'),
                    value: /^[a-zA-Z0-9-]+$/,
                  },
                })}
                label={t('menuMaker:productForm.barcodeLabel')}
                name="barcodeField"
                onChange={({ target }) => handleOnChangeBarcodeField(target, 'barcodeField')}
                onKeyPress={handleValidateChars}
                placeholder={t('common:messages.optional')}
              />

              <Box mb={1} mt={4}>
                <Divider />
              </Box>
            </Grid>

            <Grid item>
              <Typography className={clsx(classes.satOptional)}>
                <Trans i18nKey="menuMaker:productForm.tabAdvanced.satOptional" />
              </Typography>

              <Typography className={classes.satDefaultsInfo}>
                <Trans i18nKey="menuMaker:productForm.tabAdvanced.satDefaultsInfo" />
              </Typography>

              <Controller
                control={control}
                name="taxServingUnitField"
                render={({ onChange, value, name, ref }) => (
                  <SelectDS
                    data-testid="taxServingUnitField"
                    displayEmpty
                    fullWidth
                    inputRef={ref}
                    label={t('menuMaker:productForm.unitMeasurement')}
                    name={name}
                    onChange={onChange}
                    renderValue={(selected) => renderServingUnitItem(selected)}
                    value={value}
                  >
                    {taxServingUnitTypes.map((taxKey) => (
                      <MenuItem data-testid={taxKey.key} key={taxKey.key} value={taxKey.key}>
                        {taxKey.label}
                      </MenuItem>
                    ))}
                  </SelectDS>
                )}
              />
            </Grid>

            <Grid item>
              <Box className={classes.wrapperMultiSelect} display="grid">
                <SatProductKeyPicker
                  defaultValue={DEFAULT_SAT_PRODUCT_KEY}
                  label={t('menuMaker:productForm.productCatalogue')}
                  onChange={(next) => {
                    setValue('taxProductKeyField', next?.code ?? '', { shouldDirty: true });
                    setValue('taxProductKeyDescriptionField', next?.description ?? '', { shouldDirty: true });
                  }}
                  value={satSelection}
                />
              </Box>
            </Grid>

            <Grid
              className={clsx({ [classes.hideElements]: watchFields?.soldAloneField === SalesTypes.WITH_MODIFIER })}
              item
            >
              <Box mb={4} mt={1}>
                <Divider />
              </Box>

              <SettingsAdvanceTaxes control={control} register={register} setValue={setValue} taxesGroup={multiTaxes} />

              <Box mt={4}>
                <Divider />
              </Box>
            </Grid>

            <Grid
              className={clsx({ [classes.hideElements]: watchFields?.soldAloneField === SalesTypes.WITH_MODIFIER })}
              item
            >
              <KitchenAreasAsigned
                canSelectOtherArea={canSelectOtherAreas}
                cantPrintKA={cantPrintKA}
                control={control}
                kitchenAreas={kitchenAreasState.list}
                kitchenAreasFieldsCanPrint={watchFields?.kitchenAreasFieldsCanPrint}
                loading={isLoading}
                onChangeCanSelectAreas={handleCanSelectAreas}
                register={register}
                setCantPrintKA={setCantPrintKA}
                t={t}
              />
            </Grid>
          </Grid>

          {showConfirmAreasKitchen.show && (
            <ConfirmDialog
              id="dialogConfirmKitchenAreas"
              labelAccept={showConfirmAreasKitchen.isAddMode ? t('common:buttons.confirm') : t('common:buttons.accept')}
              labelCancel={t('common:buttons.cancel')}
              message={showConfirmAreasKitchen.message}
              onAccept={handleConfirmNewAreasKitchen}
              onCancel={handleCancelConfirmNewAreasKitchen}
              open={showConfirmAreasKitchen.show}
              title={t('menuMaker:dialogConfirmKitchenAreas.title')}
            >
              {!canSelectOtherAreas && virtualKitchenAreas?.length > 0 && showConfirmAreasKitchen.isAddMode && (
                <KitchenAreasBox
                  kitchenAreasNames={getKitchenAreasDetail(virtualKitchenAreas, kitchenAreasState.list).map(
                    (ka) => ka.name,
                  )}
                />
              )}
            </ConfirmDialog>
          )}
        </TabPanel>
      </Grid>
    );
  }

  return null;
}

ProductForm.propTypes = {
  isEdition: PropTypes.bool,
  isLoading: PropTypes.bool,
  isOrganizationMode: PropTypes.bool,
  categoriesState: PropTypes.object,
  kitchenAreasState: PropTypes.object,
  modifierGroupsState: PropTypes.object,
  openDrawer: PropTypes.bool,
  canSelectOtherAreas: PropTypes.bool,
  setCanSelectOtherAreas: PropTypes.func,
  isLoaded: PropTypes.bool,
  cantPrintKA: PropTypes.bool,
  setCantPrintKA: PropTypes.func,
  defaultTaxes: PropTypes.object,
  multiTaxes: PropTypes.array,
  menusProductOverride: PropTypes.array,
  uuidSelected: PropTypes.string,
  categoryOfProduct: PropTypes.string,
  actionState: PropTypes.object,
};

export default ProductForm;
